import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { TransactionsFilter, TransactionStatus, TransactionType } from '../../models/TransactionsModels';

import { resetFilter, setFilter } from '../../stores/TransactionsStore';
import { RootState } from '../../stores/Store';

import { DropdownItem } from '../../components/Dropdown/DropdownModels';
import Dropdown from '../../components/Dropdown/Dropdown';
import Button from '../../components/Button/Button';
import ButtonGroup from '../../components/ButtonGroup/ButtonGroup';
import RefreshIcon from '../../components/Icons/RefreshIcon';
import TransactionCounters from './components/TransactionCounters';
import SearchInput from '../../components/SearchInput/SearchInput';
import ClickableLinkWithIcon from '../../components/Text/ClickableLinkWithIcon';
import FilterIcon from '../../components/Icons/FilterIcon';
import { SuccessTickIconTable } from '../../components/Icons/SuccessTickIcon';
import ResetIcon from '../../components/Icons/ResetIcon';
import { FiltersBox, FilterTagsList } from '../../components/FilterTag/FilterTagStyle';

import useTransactionsTableFilters from './useTransactionsTableFilters';
import DownloadMenu from '../../components/DownloadMenu/DownloadMenu';
import usePortalConfig from '../../hooks/usePortalConfig/usePortalConfig';
import { FileFormat } from '../../models/ImportModels';

interface TransactionsTableFiltersProps {
  fetchTransactions: () => void;
  onSearch: () => void;
  isLoading: boolean;
  exportData: (format: FileFormat) => void;
  totalItems: number;
  resetSort: () => void;
}

function TransactionsTableFilters({
  fetchTransactions,
  onSearch,
  isLoading = false,
  exportData,
  totalItems,
  resetSort,
}: TransactionsTableFiltersProps) {
  const { configs } = usePortalConfig();
  const theme = useTheme();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { filter } = useSelector((state: RootState) => state.transactionsReducer);
  const [showFiltersArea, setShowFiltersArea] = useState(false);
  const { transactionTypesOptions, transactionCounters, countersLoading, fetchTransactionCounters, renderFilterTags } =
    useTransactionsTableFilters();

  const statusOptions: DropdownItem[] = Object.keys(TransactionStatus)
    .sort()
    .map((st) => ({
      value: st,
      key: st,
      label: t(`pages.transactions.table.status.${st}`),
    }));

  /**
   * Search handler
   * @param f (filter)
   * @param type (TransactionType)
   */
  const search = (f: string) => {
    onSearch();
    dispatch(setFilter({ ...filter, filter: f }));
  };

  /**
   * Refreshes transaction list and counters
   */
  const handleRefresh = () => {
    fetchTransactionCounters();
    fetchTransactions();
  };

  /**
   * Apply filters
   */
  const applyFilters = (vals: TransactionsFilter) => {
    dispatch(setFilter(vals));
    setShowFiltersArea(false);
  };

  const formik = useFormik({
    initialValues: {
      status: '',
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      applyFilters({ ...filter, ...formik.values });
    },
  });

  /**
   * Resets the form to the initial vals
   */
  const resetForm = () => {
    formik.resetForm();
    dispatch(resetFilter());
  };

  useEffect(() => {
    if (!filter.status) formik.resetForm();
  }, [filter.status]);

  return (
    <>
      <Grid container columnSpacing="1.5rem" rowSpacing="1.5rem" sx={{ paddingTop: '1.5rem' }}>
        <Grid item xl={5} md={6} xs={12}>
          <Dropdown
            id="type"
            name="type"
            label={t('pages.transactions.transactionType')}
            value={filter.type}
            onChange={(e) => {
              dispatch(setFilter({ ...filter, type: e.target.value as TransactionType }));
              resetSort();
            }}
            items={transactionTypesOptions}
            placeholder={
              t('common.placeholders.dropdownPlaceholder') + t('pages.transactions.transactionType').toLowerCase()
            }
          />
        </Grid>
        <Grid item xl={7} md={6} xs={12}>
          <Box sx={{ display: { xl: 'flex', md: 'flex', xs: 'inline-block' }, alignItems: 'end', height: '100%' }}>
            <Button
              sx={{ marginLeft: 'auto' }}
              onClick={handleRefresh}
              label={
                <>
                  <Box sx={{ marginRight: '0.5rem' }}>
                    <RefreshIcon />
                  </Box>
                  {t('pages.transactions.updateList')}
                </>
              }
            />
          </Box>
        </Grid>
      </Grid>
      <Divider
        sx={{ borderColor: theme.palette.monochrome.monoChrome3, marginTop: '2.5rem', marginBottom: '1.5rem' }}
      />
      <TransactionCounters counters={transactionCounters} isLoading={isLoading || countersLoading} />
      {filter.type !== '' &&
        filter.type !== TransactionType.BULK_NEW_CONNECTION &&
        filter.type !== TransactionType.BULK_SIM_SWAP && (
          <Grid container sx={{ paddingBottom: '1rem' }}>
            <Grid item xl={5} md={6} xs={12}>
              <SearchInput
                onSearch={(q) => {
                  search(q);
                }}
                placeholder={t('pages.transactions.table.filters.searchPlaceholder')}
                isButtonDisabled={isLoading}
                value={filter.filter}
              />
            </Grid>
          </Grid>
        )}
      <FilterTagsList sx={{ padding: '1rem 0' }}>{renderFilterTags().map((n) => n)}</FilterTagsList>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body1" paddingBottom="1rem">
          {t('common.table.totalItems', { count: totalItems })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <ClickableLinkWithIcon
            wrapperSx={{ paddingLeft: '1rem', paddingRight: '1rem' }}
            sx={{ color: theme.palette.text.primary }}
            onClick={() => setShowFiltersArea(!showFiltersArea)}
            leftIcon={<FilterIcon />}
            disabled={isLoading}
          >
            {t('common.filter')}
          </ClickableLinkWithIcon>
          <DownloadMenu
            exportMaxNumberOfRows={configs.transactionsReport.exportMaxNumberOfRows}
            disabled={isLoading || totalItems === 0}
            downloadCSV={() => exportData('csv')}
            downloadExcel={() => exportData('xlsx')}
            itemLength={totalItems}
          />
        </Box>
      </Box>
      {showFiltersArea && (
        <FiltersBox>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ paddingBottom: '1.5rem' }}>
              <Dropdown
                id="status"
                name="status"
                label={t('pages.auditTrail.table.headers.status')}
                value={formik.values.status}
                onChange={(e) => formik.handleChange(e)}
                items={statusOptions}
                formStyle={{ maxWidth: '23.875rem' }}
                placeholder={
                  t('common.placeholders.dropdownPlaceholder') +
                  t('pages.auditTrail.table.headers.status').toLowerCase()
                }
              />
            </Box>
            <Box sx={{ marginTop: 'auto' }}>
              <ButtonGroup>
                <Button
                  label={
                    <>
                      <Box sx={{ marginRight: '0.5rem' }}>
                        <SuccessTickIconTable stroke={theme.palette.secondary.contrastText} />
                      </Box>
                      {t('common.applyFilters')}
                    </>
                  }
                  color="primaryButton"
                  disabled={!formik.isValid}
                  onClick={formik.submitForm}
                />
                <Button
                  label={
                    <>
                      <Box sx={{ marginRight: '0.5rem' }}>
                        <ResetIcon stroke={theme.palette.secondary.contrastText} />
                      </Box>
                      {t('common.resetFilters')}
                    </>
                  }
                  color="secondaryButton"
                  onClick={resetForm}
                />
              </ButtonGroup>
            </Box>
          </form>
        </FiltersBox>
      )}
    </>
  );
}

export default TransactionsTableFilters;
